import { SetStateAction, useCallback, useEffect, useState } from 'react'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'

import { setModalTitleAction, useAppDispatch } from 'store'
import { useSteps } from 'App/hooks'
import { TAccountsSelectOptionData } from 'App/components/common/AccountsSelect/AccountsSelectOption/AccountsSelectOption'
import { IDepartment, TChannelVariant } from 'interfaces'

enum steps {
  MAIN = 0,
  MEMBERS = 1,
  CHANEL_MANAGER = 2
}

export type TUseUnityHubCreateChannel = {
  channelId?: string
  channelName?: string
  isPublic?: boolean
  variant?: TChannelVariant
  maxSelectedChannelManager?: number
}

export type TChannelDepartment = Pick<
  IDepartment,
  'id' | 'name' | 'isChatsParticipant' | 'isVoiceRoomsParticipant'
>

export const useUnityHubCreateChannel = ({
  maxSelectedChannelManager,
  channelId = '',
  channelName = '',
  isPublic: channelIsPublic,
  variant = 'chat'
}: TUseUnityHubCreateChannel) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { step, setStep } = useSteps(steps.MAIN)

  const [name, setName] = useState<string>(channelName)
  const [isPublic, setIsPublic] = useState<boolean>(channelIsPublic || false)

  const [channelMembers, setChannelMembers] = useState<TAccountsSelectOptionData[]>([])
  const [channelManagers, setChannelManagers] = useState<TAccountsSelectOptionData[]>([])
  const [channelDepartments, setChannelDepartments] = useState<TChannelDepartment[]>([])

  const [prefetchedAccounts, setPrefetchedAccounts] = useState<TAccountsSelectOptionData[]>([])
  const [prefetchedChannelManagers, setPrefetchedChannelManagers] = useState<
    TAccountsSelectOptionData[]
  >([])
  const [prefetchedChannelDepartments, setPrefetchedChannelDepartments] = useState<
    TChannelDepartment[]
  >([])

  const [dirtyManagers, setDirtyManagers] = useState(false)
  const [dirtyAccounts, setDirtyAccounts] = useState(false)
  const [dirtyDepartments, setDirtyDepartments] = useState(false)
  const [dirtyName, setDirtyName] = useState(false)
  const [dirtyIsPublic, setDirtyIsPublic] = useState(false)

  const isEdit = Boolean(channelId && channelName)

  const handleOpenMain = useCallback(() => {
    setStep(steps.MAIN)

    const textTitle = isEdit
      ? t('modal.createChannel.edit.title_chat')
      : t('modal.createChannel.create.title_chat')
    const voiceTitle = isEdit
      ? t('modal.createChannel.edit.title_voice')
      : t('modal.createChannel.create.title_voice')

    dispatch(setModalTitleAction({ modalTitle: variant === 'chat' ? textTitle : voiceTitle }))
  }, [dispatch, isEdit, setStep, variant, t])

  const handleOpenMembers = useCallback(() => {
    setStep(steps.MEMBERS)
    dispatch(setModalTitleAction({ modalTitle: t('modal.createChannel.selectMembers.title') }))
  }, [dispatch, setStep, t])

  const handleOpenChanelManager = useCallback(() => {
    setStep(steps.CHANEL_MANAGER)
    dispatch(
      setModalTitleAction({ modalTitle: t('modal.createChannel.selectChannelManager.title') })
    )
  }, [dispatch, setStep, t])

  const dedupeById = (arr: TAccountsSelectOptionData[]) => {
    const seen = new Set<string>()
    return arr.filter((x) => {
      if (seen.has(x.id)) {
        return false
      }
      seen.add(x.id)
      return true
    })
  }

  const clampByMax = (
    arr: TAccountsSelectOptionData[],
    max?: number
  ): TAccountsSelectOptionData[] => {
    if (!max || max <= 0) {
      return arr
    }
    return arr.slice(0, max)
  }

  const handleSetChannelManagers = useCallback(
    (
      value?: TAccountsSelectOptionData[] | SetStateAction<TAccountsSelectOptionData[]>,
      prefetched = false
    ) => {
      setChannelManagers((prevManagers) => {
        const incoming = typeof value === 'function' ? (value as any)(prevManagers) : value || []

        const normalized = clampByMax(dedupeById(incoming), maxSelectedChannelManager)

        if (prefetched) {
          setPrefetchedChannelManagers(normalized)
        }

        setChannelMembers((prevMembers) => dedupeById([...prevMembers, ...normalized]))

        return normalized
      })
    },
    [maxSelectedChannelManager]
  )

  const handleSetChannelMembers = useCallback(
    (value: SetStateAction<TAccountsSelectOptionData[]>, prefetched = false) => {
      setChannelMembers((prev) => {
        const next = typeof value === 'function' ? (value as any)(prev) : value || []
        if (prefetched) {
          setPrefetchedAccounts(next)
        }
        return next
      })
    },
    []
  )

  const handleSetChannelDepartments = useCallback(
    (value: SetStateAction<TChannelDepartment[]>, prefetched = false) => {
      setChannelDepartments((prev) => {
        const next = typeof value === 'function' ? (value as any)(prev) : value || []
        if (prefetched) {
          setPrefetchedChannelDepartments(next)
        }
        return next
      })
    },
    []
  )

  useEffect(() => {
    setDirtyAccounts(
      prefetchedAccounts.length !== channelMembers.length ||
        !isEqual(prefetchedAccounts, channelMembers)
    )
  }, [prefetchedAccounts, channelMembers])

  useEffect(() => {
    setDirtyDepartments(
      prefetchedChannelDepartments.length !== channelDepartments.length ||
        !isEqual(prefetchedChannelDepartments, channelDepartments)
    )
  }, [prefetchedChannelDepartments, channelDepartments])

  useEffect(() => {
    setDirtyManagers(
      prefetchedChannelManagers.length !== channelManagers.length ||
        !isEqual(prefetchedChannelManagers, channelManagers)
    )
  }, [prefetchedChannelManagers, channelManagers])

  useEffect(() => {
    setDirtyName(channelName !== name)
  }, [channelName, name])

  useEffect(() => {
    setDirtyIsPublic(channelIsPublic !== isPublic)
  }, [channelIsPublic, isPublic])

  return {
    dirty: dirtyName || dirtyAccounts || dirtyManagers || dirtyIsPublic || dirtyDepartments,
    name,
    channelMembers,
    channelManagers,
    channelDepartments,
    step,
    isPublic,
    dirtyIsPublic,
    isEdit,
    handleOpenMain,
    handleOpenMembers,
    handleOpenChanelManager,
    setIsPublic,
    setName,
    setChannelMembers: handleSetChannelMembers,
    setChannelManagers: handleSetChannelManagers,
    setChannelDepartments: handleSetChannelDepartments
  }
}
